// src/modules/staff/components/StaffTable.tsx
import React, { useMemo } from "react";
import { Tag } from "antd";
import type { ColumnsType } from "antd/es/table";
import { useTranslation } from "react-i18next";
import Datatable from "components/Common/dataTable";
import { Staff } from "types/staffTypes";
import UserProfileHeader from "components/Common/ui/UserProfileHeader";
import { formatMoney } from "helpers/numberHelper";
import { formatToLocaleDate } from "utils/dateUtils";
import UserRoleBadge from "components/Common/Badge/UserRoleBadge";
import ActionsDropdown, { Action } from "components/Common/ActionsDropdown";
import { all_routes } from "router/all_routes";
import { useAbility } from "config/abilityContext";
import { showConfirmDialog } from "utils/confirmDialogUtils";

type Props = {
    dataSource: Staff[];
    loading?: boolean;
    onView?: (row: Staff) => void;
    onEdit?: (row: Staff) => void;
    onDelete?: (id: string) => void;
    dataPaginate: any;
    onChange: (pg: any) => void;
};

const CONTRACT_COLORS: Record<string, "default" | "green" | "orange" | "purple" | "blue" | "red"> = {
    CDI: "green",
    CDD: "orange",
    Stage: "blue",
    Other: "purple",
};

const StaffTable: React.FC<Props> = ({ dataSource, loading, onView, onEdit, onDelete, dataPaginate, onChange }) => {
    const { t } = useTranslation();
    const ability = useAbility();

    const columns = [
        {
            title: t("user"),
            key: "user",
            render: (v: string, r: Staff) => <UserProfileHeader firstName={r.user?.firstName || ""} lastName={r.user?.lastName || ""} photo={r.user?.photo || ""} subtitle={r.user?.email || ""} />,
        },
        {
            title: t("role"),
            align: "center" as const,
            render: (v: string, r: Staff) => <UserRoleBadge role={r.user?.role || ""} size="sm" />,
        },
        {
            title: t("staff.form.contractType"),
            dataIndex: "contract",
            key: "contract",
            align: "center" as const,
            render: (contract: string) => (
                <Tag color={CONTRACT_COLORS[contract] ?? "default"}>{contract}</Tag>
            ),
        },
        {
            title: t("staff.form.monthlySalary"),
            dataIndex: "monthlySalary",
            key: "monthlySalary",
            align: "center" as const,
            render: (v: number | null) => (formatMoney(v ?? 0)),
        },
        // {
        //     title: t("staff.form.startDate"),
        //     dataIndex: "startDate",
        //     key: "startDate",
        //     align: "center" as const,
        //     render: (v: string) => formatToLocaleDate(v) ?? "—",
        // },
        // {
        //     title: t("staff.form.endDate"),
        //     dataIndex: "endDate",
        //     key: "endDate",
        //     align: "center" as const,
        //     render: (v: string | null) => formatToLocaleDate(v) ?? "—",
        // },
        {
            title: t("staff.form.jobPosition"),
            dataIndex: "jobPositionId",
            key: "jobPositionId",
            align: "center" as const,
            render: (v: string, r: Staff) => r.jobPosition?.code ? t("job_positions." + r.jobPosition?.code) : "—",
        },
        {
            title: t("staff.form.createdAt"),
            dataIndex: "createdAt",
            key: "createdAt",
            align: "center" as const,
            render: (v: string) => formatToLocaleDate(v) ?? "—",
        },
        {
            title: t("actions"),
            key: "actions",
            align: "center" as const,
            render: (v: string, r: Staff) => {
                const actions: Action[] = [
                    {
                        label: t("edit"),
                        icon: "ti ti-edit",
                        actionType: "link",
                        hidden: !ability.can("update", "Staff"),
                        to: all_routes.staffEdit.replace(":id", r.id),
                    },
                    {
                        label: t("delete"),
                        icon: "ti ti-trash",
                        actionType: "link",
                        onClick: () => showConfirmDialog({
                            title: t("are_you_sure_you_want_to_delete_this_staff?"),
                            content: t("this_action_cannot_be_undone"),
                            okText: t("delete"),
                            okType: "danger",
                            cancelText: t("cancel"),
                            onOk: () => onDelete && onDelete(r.id),
                        }),
                        hidden: !onDelete || !ability.can("delete", "Staff"),
                    },
                ];
                return <ActionsDropdown actions={actions} />;
            },
        },
    ]

    return (
        <div className="table-responsive">
            <Datatable
                rowKey="id"
                columns={columns}
                dataSource={dataSource}
                loading={loading}
                pagination={true}
                dataPaginate={dataPaginate}
                onChange={onChange}
            />
        </div>
    );
};

export default StaffTable;
